import { useState, useEffect } from 'react'
import { useGenerator } from '../../../../hooks/useGenerator'
import { useSettingsStore } from '../../../../stores/settingsStore'
import { useImagePromptStore } from '../../../../stores/imagePromptStore'
import { 
  Button,
  Tabs, TabsContent, TabsList, TabsTrigger,
  Label,
  RadioGroup, RadioGroupItem,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  Textarea,
  Checkbox,
  Alert, AlertDescription,
  Slider
} from '@creanizer/ui'
import { Copy, Download, Save, AlertCircle, Sparkles, Archive, Settings, Edit, Check, X, Languages, Upload, Image } from 'lucide-react'
import type { ImagePromptInput, ImagePromptResult, SavedImagePrompt, AIModel } from '@creanizer/types'
import { ManualInputTabs } from './ManualInputTabs'
import { SavedPromptsList } from './SavedPromptsList'
import { PresetManager } from './PresetManager'
import { ImageGeneratePanel } from './ImageGeneratePanel'
import { apiClient } from '../../../../services/api'

export function ImagePromptGenerator() {
  const { isApiKeySet } = useSettingsStore()
  const { 
    savedPrompts, 
    savePrompt, 
    updatePrompt,
    deletePrompt,
    deleteAllPrompts,
    exportAsText,
    exportAsJSON,
    importFromJSON,
    init 
  } = useImagePromptStore()
  const generator = useGenerator('imagePrompt')
  
  // ストアの初期化
  useEffect(() => {
    init()
  }, [init])
  
  // 基本設定
  const [imageType, setImageType] = useState<'portrait' | 'illustration' | 'background' | 'object'>('portrait')
  const [composition, setComposition] = useState<'face' | 'upper_body' | 'waist_up' | 'full_body'>('waist_up')
  const [backgroundType, setBackgroundType] = useState<'scene' | 'solid_color'>('solid_color')
  const [backgroundColor, setBackgroundColor] = useState('#FFFFFF')
  
  // 入力ソース
  const [inputSource, setInputSource] = useState<'manual' | 'text_upload'>('manual')

  // 手動入力データ
  const [manualInput, setManualInput] = useState({})

  // テキストファイルアップロード
  const [uploadedText, setUploadedText] = useState<string>('')

  // 画像タイプ切り替え時のクリア処理
  const handleImageTypeChange = (newType: 'portrait' | 'illustration' | 'background' | 'object') => {
    setImageType(newType)
    setManualInput({}) // 手動入力をクリア
    setUploadedText('') // テキストもクリア

    // 画像タイプごとのデフォルト値を設定
    if (newType === 'portrait') {
      setComposition('waist_up')
      setBackgroundType('solid_color')
      setBackgroundColor('#FFFFFF')
    } else if (newType === 'object') {
      setBackgroundType('solid_color')
      setBackgroundColor('#FFFFFF')
    }
  }

  // 入力方法切り替え時のクリア処理
  const handleInputSourceChange = (newSource: 'manual' | 'text_upload') => {
    setInputSource(newSource)
    if (newSource === 'text_upload') {
      setManualInput({}) // 手動入力をクリア
    } else {
      setUploadedText('') // テキストをクリア
    }
  }

  // テキストファイルアップロード処理
  const handleTextFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // ファイルタイプチェック
    if (!file.type.startsWith('text/') && !file.name.endsWith('.txt')) {
      setError('テキストファイル(.txt)を選択してください')
      return
    }

    const reader = new FileReader()
    reader.onload = (event) => {
      const content = event.target?.result as string
      setUploadedText(content)
      setError(null)
    }
    reader.onerror = () => {
      setError('ファイルの読み込みに失敗しました')
    }
    reader.readAsText(file)
    e.target.value = '' // リセット
  }

  // 出力設定
  const [targetModel, setTargetModel] = useState<AIModel>('novelai_v45')
  const [creativityLevel, setCreativityLevel] = useState(5)
  const [useProMode, setUseProMode] = useState(false)
  const [useGemini3FlashMode, setUseGemini3FlashMode] = useState(false)

  // 結果
  const [result, setResult] = useState<ImagePromptResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  // 編集モード
  const [isEditMode, setIsEditMode] = useState(false)
  const [editedPositivePrompt, setEditedPositivePrompt] = useState('')
  const [editedNegativePrompt, setEditedNegativePrompt] = useState('')

  // 翻訳表示
  const [showTranslation, setShowTranslation] = useState(false)

  // 画像アップロード
  const [uploadedImage, setUploadedImage] = useState<string | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [isAnalyzing, setIsAnalyzing] = useState(false)

  // 再翻訳
  const [isRetranslating, setIsRetranslating] = useState(false)
  
  const handleGenerate = async () => {
    if (!isApiKeySet()) {
      setError('APIキーが設定されていません')
      return
    }
    
    setError(null)
    
    const input: ImagePromptInput = {
      imageType,
      composition: imageType === 'portrait' ? composition : undefined,
      backgroundType: (imageType === 'portrait' || imageType === 'object') ? backgroundType : undefined,
      backgroundColor: backgroundType === 'solid_color' ? backgroundColor : undefined,
      inputSource,
      manualInput: inputSource === 'manual' ? manualInput : undefined,
      uploadedText: inputSource === 'text_upload' ? uploadedText : undefined,
      targetModel,
      creativityLevel,
      useProMode,
      useGemini3FlashMode,
      seed: Math.floor(Math.random() * 1000000)
    }

    try {
      const response = await generator.mutateAsync(input)
      setResult(response.result as ImagePromptResult)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'プロンプト生成に失敗しました')
    }
  }
  
  const isSaved = () => {
    if (!result || !result.positivePrompt) return false
    return savedPrompts.some(saved => 
      saved?.result?.positivePrompt === result.positivePrompt &&
      saved?.result?.negativePrompt === result.negativePrompt
    )
  }
  
  const handleSave = async () => {
    if (!result) return
    
    const savedPrompt: SavedImagePrompt = {
      id: crypto.randomUUID(),
      title: `${getModelDisplayName(targetModel)} - ${new Date().toLocaleDateString('ja-JP')}`,
      input: {
        imageType,
        composition,
        backgroundType,
        backgroundColor,
        inputSource,
        manualInput: inputSource === 'manual' ? manualInput : undefined,
        uploadedText: inputSource === 'text_upload' ? uploadedText : undefined,
        targetModel,
        creativityLevel,
        useProMode,
        useGemini3FlashMode
      },
      result,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }

    try {
      await savePrompt(savedPrompt)
      alert('プロンプトを保存しました')
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存に失敗しました')
    }
  }

  const handleLoadPrompt = (prompt: SavedImagePrompt) => {
    // 保存されたプロンプトの設定を復元
    setImageType(prompt.input.imageType)
    if (prompt.input.composition) setComposition(prompt.input.composition)
    if (prompt.input.backgroundType) setBackgroundType(prompt.input.backgroundType)
    if (prompt.input.backgroundColor) setBackgroundColor(prompt.input.backgroundColor)

    // 入力ソースに応じて復元
    if (prompt.input.inputSource) {
      setInputSource(prompt.input.inputSource as 'manual' | 'text_upload')
    }
    if (prompt.input.manualInput) setManualInput(prompt.input.manualInput)
    if (prompt.input.uploadedText) setUploadedText(prompt.input.uploadedText)

    setTargetModel(prompt.input.targetModel)
    if (prompt.input.creativityLevel) setCreativityLevel(prompt.input.creativityLevel)
    if (prompt.input.useProMode !== undefined) setUseProMode(prompt.input.useProMode)
    if (prompt.input.useGemini3FlashMode !== undefined) setUseGemini3FlashMode(prompt.input.useGemini3FlashMode)
    setResult(prompt.result)
  }
  
  const handleLoadPreset = (settings: any) => {
    setManualInput(settings)
  }
  
  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text)
  }

  const handleStartEdit = () => {
    if (result) {
      setEditedPositivePrompt(result.positivePrompt)
      setEditedNegativePrompt(result.negativePrompt)
      setIsEditMode(true)
    }
  }

  const handleCancelEdit = () => {
    setIsEditMode(false)
    setEditedPositivePrompt('')
    setEditedNegativePrompt('')
  }

  const handleApplyEdit = () => {
    if (result) {
      setResult({
        ...result,
        positivePrompt: editedPositivePrompt,
        negativePrompt: editedNegativePrompt
      })
      setIsEditMode(false)
    }
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // ファイルタイプチェック
    if (!file.type.startsWith('image/')) {
      setError('画像ファイルを選択してください')
      return
    }

    try {
      const reader = new FileReader()
      const base64 = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string)
        reader.onerror = reject
        reader.readAsDataURL(file)
      })

      setUploadedImage(base64)
      setImagePreview(base64)
      setError(null)
    } catch (err) {
      setError('画像の読み込みに失敗しました')
    }

    e.target.value = '' // リセット
  }

  const handleImageAnalyze = async () => {
    if (!uploadedImage) {
      setError('画像がアップロードされていません')
      return
    }

    if (!isApiKeySet()) {
      setError('APIキーが設定されていません')
      return
    }

    setIsAnalyzing(true)
    setError(null)

    const input: ImagePromptInput = {
      imageType,
      composition: imageType === 'portrait' ? composition : undefined,
      backgroundType: (imageType === 'portrait' || imageType === 'object') ? backgroundType : undefined,
      backgroundColor: backgroundType === 'solid_color' ? backgroundColor : undefined,
      inputSource: 'image_upload',
      uploadedImage,
      manualInput: undefined,
      uploadedText: undefined,
      targetModel,
      creativityLevel,
      useProMode,
      useGemini3FlashMode,
      seed: Math.floor(Math.random() * 1000000)
    }

    try {
      const response = await generator.mutateAsync(input)
      setResult(response.result as ImagePromptResult)

      // 分析結果をmanualInputのadditionalInfo.textに設定
      // これにより、ユーザーが編集可能になる
      // (実際の分析テキストはバックエンドで生成される)
    } catch (err) {
      setError(err instanceof Error ? err.message : '画像分析に失敗しました')
    } finally {
      setIsAnalyzing(false)
    }
  }

  const handleImageClear = () => {
    setUploadedImage(null)
    setImagePreview(null)
  }

  const handleRetranslate = async () => {
    if (!result) return

    if (!isApiKeySet()) {
      setError('APIキーが設定されていません')
      return
    }

    setIsRetranslating(true)
    setError(null)

    try {
      // 編集モードの場合は編集後のプロンプトを使用、そうでない場合は現在のプロンプトを使用
      const positiveToTranslate = isEditMode ? editedPositivePrompt : result.positivePrompt
      const negativeToTranslate = isEditMode ? editedNegativePrompt : result.negativePrompt

      // apiClientを使って翻訳エンドポイントを呼び出し
      const response = await apiClient.translatePrompt({
        positivePrompt: positiveToTranslate,
        negativePrompt: negativeToTranslate,
        targetModel
      })

      // 翻訳結果を更新
      setResult({
        ...result,
        positivePrompt: positiveToTranslate,
        negativePrompt: negativeToTranslate,
        translatedPositivePrompt: response.result.translatedPositivePrompt,
        translatedNegativePrompt: response.result.translatedNegativePrompt
      })

      // 編集モードを終了
      if (isEditMode) {
        setIsEditMode(false)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '再翻訳に失敗しました')
    } finally {
      setIsRetranslating(false)
    }
  }

  const handleExport = () => {
    if (!result) return
    
    const exportData = {
      positivePrompt: result.positivePrompt,
      negativePrompt: result.negativePrompt,
      modelSpecificParams: result.modelSpecificParams,
      metadata: {
        model: targetModel,
        imageType,
        creativityLevel,
        generatedAt: new Date().toISOString()
      }
    }
    
    const dataStr = JSON.stringify(exportData, null, 2)
    const dataBlob = new Blob([dataStr], { type: 'application/json' })
    const url = URL.createObjectURL(dataBlob)
    
    const link = document.createElement('a')
    link.href = url
    link.download = `prompt-${targetModel}-${Date.now()}.json`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    
    URL.revokeObjectURL(url)
  }
  
  const getModelDisplayName = (model: AIModel) => {
    const modelNames: Record<AIModel, string> = {
      'novelai_v45': 'NovelAI V4.5',
      'midjourney_v7': 'Midjourney V7',
      'sdxl_standard': 'Stable Diffusion XL',
      'pony_v6': 'Pony V6 XL',
      'animagine_v4': 'Animagine XL 4.0',
      'illustrious': 'Illustrious XL',
      'noobai_xl': 'NoobAI XL'
    }
    return modelNames[model]
  }
  
  return (
    <div className="space-y-6">
      <div className="card">
        <h2 className="text-xl font-semibold mb-2">画像プロンプトジェネレータ</h2>
        <p className="text-muted-foreground mb-6">
          AI画像生成用のプロンプトを自動生成します
        </p>
      </div>
      
      <Tabs defaultValue="generate">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="generate">
            <Sparkles className="h-4 w-4 mr-2" />
            生成
          </TabsTrigger>
          <TabsTrigger value="saved">
            <Archive className="h-4 w-4 mr-2" />
            保存済み ({savedPrompts.length})
          </TabsTrigger>
          <TabsTrigger value="presets">
            <Settings className="h-4 w-4 mr-2" />
            プリセット
          </TabsTrigger>
          <TabsTrigger value="image-generate">
            <Image className="h-4 w-4 mr-2" />
            画像生成
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="generate" className="space-y-6">
          <div className="card">
            <div className="pt-6 space-y-6">
              {/* 画像タイプ選択 */}
              <div className="space-y-2">
                <Label>画像タイプ</Label>
                <RadioGroup value={imageType} onValueChange={(v) => handleImageTypeChange(v as any)}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="portrait" id="portrait" />
                    <Label htmlFor="portrait">立ち絵（顔〜全身）</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="illustration" id="illustration" />
                    <Label htmlFor="illustration">一枚絵・シーン（動きのある構図）</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="object" id="object" />
                    <Label htmlFor="object">オブジェクト・アイテム（物体のみ）</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="background" id="background" />
                    <Label htmlFor="background">背景のみ</Label>
                  </div>
                </RadioGroup>
              </div>
              
              {/* 立ち絵設定 */}
              {imageType === 'portrait' && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>構図</Label>
                    <Select value={composition} onValueChange={(v) => setComposition(v as any)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="face">顔アップ</SelectItem>
                        <SelectItem value="upper_body">バストアップ</SelectItem>
                        <SelectItem value="waist_up">ウエストアップ</SelectItem>
                        <SelectItem value="full_body">全身</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>背景設定</Label>
                    <RadioGroup value={backgroundType} onValueChange={(v) => setBackgroundType(v as any)}>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="solid_color" id="solid_color" />
                        <Label htmlFor="solid_color">単色背景</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="scene" id="scene" />
                        <Label htmlFor="scene">シーン</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {backgroundType === 'solid_color' && (
                    <div className="space-y-2">
                      <Label>背景色</Label>
                      <input
                        type="color"
                        value={backgroundColor}
                        onChange={(e) => setBackgroundColor(e.target.value)}
                        className="w-full h-10 rounded border"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* オブジェクト設定 */}
              {imageType === 'object' && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>背景設定</Label>
                    <RadioGroup value={backgroundType} onValueChange={(v) => setBackgroundType(v as any)}>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="solid_color" id="solid_color" />
                        <Label htmlFor="solid_color">単色背景</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="scene" id="scene" />
                        <Label htmlFor="scene">シーン</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {backgroundType === 'solid_color' && (
                    <div className="space-y-2">
                      <Label>背景色</Label>
                      <input
                        type="color"
                        value={backgroundColor}
                        onChange={(e) => setBackgroundColor(e.target.value)}
                        className="w-full h-10 rounded border"
                      />
                    </div>
                  )}
                </div>
              )}
              
              {/* 入力方法選択 */}
              <div className="space-y-2">
                <Label>入力方法</Label>
                <RadioGroup value={inputSource} onValueChange={(v) => handleInputSourceChange(v as any)}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="manual" id="input-manual" />
                    <Label htmlFor="input-manual">手動入力</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="text_upload" id="input-text" />
                    <Label htmlFor="input-text">テキストファイル読み込み</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* 手動入力タブ */}
              {inputSource === 'manual' && (
                <div className="space-y-4">
                  <Label>
                    {imageType === 'object' ? 'オブジェクト詳細設定' : imageType === 'background' ? '背景詳細設定' : 'キャラクター詳細設定'}
                  </Label>
                  <ManualInputTabs
                    value={manualInput}
                    onChange={setManualInput}
                    imageType={imageType}
                    uploadedImage={uploadedImage}
                    imagePreview={imagePreview}
                    isAnalyzing={isAnalyzing}
                    onImageUpload={handleImageUpload}
                    onImageAnalyze={handleImageAnalyze}
                    onImageClear={handleImageClear}
                  />
                </div>
              )}

              {/* テキストファイル読み込み */}
              {inputSource === 'text_upload' && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>テキストファイルをアップロード</Label>
                    <input
                      id="text-file-upload"
                      type="file"
                      accept=".txt,text/*"
                      onChange={handleTextFileUpload}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      onClick={() => document.getElementById('text-file-upload')?.click()}
                      className="w-full"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      テキストファイルを選択
                    </Button>
                  </div>

                  {uploadedText && (
                    <div className="space-y-2">
                      <Label>読み込んだ内容（編集可能）</Label>
                      <Textarea
                        value={uploadedText}
                        onChange={(e) => setUploadedText(e.target.value)}
                        rows={15}
                        className="font-mono text-sm"
                        placeholder="テキストファイルの内容がここに表示されます"
                      />
                      <p className="text-xs text-muted-foreground">
                        ※ キャラクターの説明、シーンの描写など、自由な形式のテキストから画像プロンプトを生成します
                      </p>
                    </div>
                  )}
                </div>
              )}
              
              {/* 出力モデル選択 */}
              <div className="space-y-2">
                <Label>出力モデル</Label>
                <Select value={targetModel} onValueChange={(v) => setTargetModel(v as AIModel)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="novelai_v45">NovelAI V4.5</SelectItem>
                    <SelectItem value="midjourney_v7">Midjourney V7</SelectItem>
                    <SelectItem value="sdxl_standard">Stable Diffusion XL</SelectItem>
                    <SelectItem value="pony_v6">Pony V6 XL</SelectItem>
                    <SelectItem value="animagine_v4">Animagine XL 4.0</SelectItem>
                    <SelectItem value="illustrious">Illustrious XL</SelectItem>
                    <SelectItem value="noobai_xl">NoobAI XL</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* 創造性レベル */}
              <div className="space-y-2">
                <Label>創造性レベル: {creativityLevel}</Label>
                <Slider
                  value={[creativityLevel]}
                  onValueChange={([v]) => setCreativityLevel(v)}
                  min={1}
                  max={10}
                  step={1}
                />
              </div>
              
              {/* Proモード */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="pro-mode"
                  checked={useProMode}
                  onCheckedChange={(checked) => {
                    setUseProMode(checked === true)
                    if (checked) setUseGemini3FlashMode(false)
                  }}
                />
                <Label htmlFor="pro-mode" className="cursor-pointer">
                  Proモード（Gemini-2.5-Pro使用）
                </Label>
              </div>

              {/* Gemini 3 Flashモード */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="gemini3-flash-mode"
                  checked={useGemini3FlashMode}
                  onCheckedChange={(checked) => {
                    setUseGemini3FlashMode(checked === true)
                    if (checked) setUseProMode(false)
                  }}
                />
                <Label htmlFor="gemini3-flash-mode" className="cursor-pointer">
                  Gemini 3 Flash モード（有料APIのみ・試験版・高速）
                </Label>
              </div>

              {/* 生成ボタン */}
              <Button
                onClick={handleGenerate}
                disabled={generator.isPending}
                className="w-full"
              >
                {generator.isPending ? '生成中...' : 'プロンプトを生成'}
              </Button>
              
              {/* エラー表示 */}
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </div>
          </div>
          
          {/* 結果表示 */}
          {result && (
            <div className="card">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">生成結果</h3>
                <div className="flex gap-2">
                  {result.translatedPositivePrompt && (
                    <Button
                      size="sm"
                      variant={showTranslation ? 'default' : 'outline'}
                      onClick={() => setShowTranslation(!showTranslation)}
                    >
                      <Languages className="h-4 w-4 mr-1" />
                      {showTranslation ? '翻訳を非表示' : '日本語翻訳を表示'}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleRetranslate}
                    disabled={isRetranslating}
                  >
                    <Languages className="h-4 w-4 mr-1" />
                    {isRetranslating ? '翻訳中...' : '再翻訳'}
                  </Button>
                  {!isEditMode ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleStartEdit}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      編集
                    </Button>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="default"
                        onClick={handleApplyEdit}
                      >
                        <Check className="h-4 w-4 mr-1" />
                        適用
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleCancelEdit}
                      >
                        <X className="h-4 w-4 mr-1" />
                        キャンセル
                      </Button>
                    </>
                  )}
                </div>
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label>ポジティブプロンプト</Label>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleCopy(isEditMode ? editedPositivePrompt : result.positivePrompt)}
                    >
                      <Copy className="h-4 w-4 mr-1" />
                      コピー
                    </Button>
                  </div>
                  <Textarea
                    value={isEditMode ? editedPositivePrompt : result.positivePrompt}
                    onChange={(e) => setEditedPositivePrompt(e.target.value)}
                    readOnly={!isEditMode}
                    className={`min-h-[100px] font-mono text-sm ${isEditMode ? 'border-blue-500' : ''}`}
                  />
                  {showTranslation && result.translatedPositivePrompt && (
                    <div className="p-3 bg-blue-50 rounded border border-blue-200">
                      <Label className="text-blue-900 mb-2 block">日本語翻訳</Label>
                      <p className="text-sm text-blue-800 whitespace-pre-wrap">
                        {result.translatedPositivePrompt}
                      </p>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label>ネガティブプロンプト</Label>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleCopy(isEditMode ? editedNegativePrompt : result.negativePrompt)}
                    >
                      <Copy className="h-4 w-4 mr-1" />
                      コピー
                    </Button>
                  </div>
                  <Textarea
                    value={isEditMode ? editedNegativePrompt : result.negativePrompt}
                    onChange={(e) => setEditedNegativePrompt(e.target.value)}
                    readOnly={!isEditMode}
                    className={`min-h-[60px] font-mono text-sm ${isEditMode ? 'border-blue-500' : ''}`}
                  />
                  {showTranslation && result.translatedNegativePrompt && (
                    <div className="p-3 bg-blue-50 rounded border border-blue-200">
                      <Label className="text-blue-900 mb-2 block">日本語翻訳</Label>
                      <p className="text-sm text-blue-800 whitespace-pre-wrap">
                        {result.translatedNegativePrompt}
                      </p>
                    </div>
                  )}
                </div>

                {/* モデル固有パラメータ */}
                {result.modelSpecificParams && Object.keys(result.modelSpecificParams).length > 0 && (
                  <div className="space-y-2">
                    <Label>モデル固有パラメータ</Label>
                    <pre className="p-2 bg-gray-100 rounded text-sm">
                      {JSON.stringify(result.modelSpecificParams, null, 2)}
                    </pre>
                  </div>
                )}

                {/* アクションボタン */}
                <div className="flex gap-2">
                  <Button
                    onClick={handleSave}
                    variant={isSaved() ? 'secondary' : 'outline'}
                    disabled={isSaved()}
                  >
                    <Save className="h-4 w-4 mr-1" />
                    {isSaved() ? '保存済み' : '保存'}
                  </Button>
                  <Button onClick={handleExport} variant="outline">
                    <Download className="h-4 w-4 mr-1" />
                    エクスポート
                  </Button>
                </div>
              </div>
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="saved" className="space-y-6">
          <SavedPromptsList
            savedPrompts={savedPrompts}
            onLoad={handleLoadPrompt}
            onUpdate={updatePrompt}
            onDelete={deletePrompt}
            onDeleteAll={deleteAllPrompts}
            onExportText={exportAsText}
            onExportJSON={exportAsJSON}
            onImportJSON={importFromJSON}
          />
        </TabsContent>
        
        <TabsContent value="presets" className="space-y-6">
          <PresetManager
            currentSettings={manualInput}
            onLoadPreset={handleLoadPreset}
          />
        </TabsContent>

        <TabsContent value="image-generate" className="space-y-6">
          <div className="card">
            <ImageGeneratePanel
              savedPrompts={savedPrompts}
              currentPrompt={result ? {
                positive: result.positivePrompt,
                negative: result.negativePrompt
              } : undefined}
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}